import { ContentSwitcher, IconButton } from "@carbon6/c6-frontend-design";
import type { DiagnosticJobAction } from "@/api/adminDiagnosticJob";
import { HatTextModal } from "@/components/modal";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { formatBytes } from "@/utils";
import { formatReportedAtAbsolute } from "./workerStatusFormatters";
import { workerJobStatusLabel } from "./workerJobStatusLabel";
import { diagnosticJobKindLabel } from "./diagnosticJobKindLabel";
import { formatWorkUnitCounts, formatWorkUnitTotal, workUnitLabel } from "./workUnitFormat";
import { DiagnosticWindowTable } from "./DiagnosticWindowTable";
import { UploadChunkTable } from "./UploadChunkTable";
import type { WorkerJobDetail } from "./useWorkerJobDetail";
import {
  useWorkerJobDetailController,
  type WorkerJobDetailModalProps,
  type JobDetailTab,
} from "./useWorkerJobDetailController";
import {
  WorkerDetailFrame,
  WorkerMetric,
  WorkerDetailSection,
  WorkerDetailField as Fact,
} from "./WorkerDetailContent";
import styles from "./WorkerDetailContent.module.css";

const labels = { cancel: "중단", restart: "재시작", resume: "이어서 실행" };
const descriptions = {
  cancel: "Worker가 현재 처리를 마친 뒤 중단할 수 있습니다.",
  restart:
    "진행을 초기화하고 처음부터 실행합니다. Worker가 요청을 반영할 때까지 기다릴 수 있습니다.",
  resume: "기존 진행을 보존하여 남은 작업부터 이어서 실행합니다.",
};
const time = (value: string | null) => (value === null ? "—" : formatReportedAtAbsolute(value));

export function WorkerJobDetailModal(props: WorkerJobDetailModalProps) {
  const controller = useWorkerJobDetailController(props);
  const { active, detail, error, refresh, pending, setPending, busy, save, close } = controller;
  return (
    <WorkerDetailFrame
      open={active}
      onClose={close}
      busy={busy}
      title={props.kind === "upload" ? "업로드 작업 상세" : "진단 작업 상세"}
      actions={
        <IconButton text="새로고침" size="sm" variant="surface" disabled={busy} onClick={refresh} />
      }
    >
      {error && (
        <EmptyState tone="error" action={{ label: "다시 시도", onClick: refresh }}>
          {error}
        </EmptyState>
      )}
      {detail === null && error === null && (
        <LoadingState>작업 상세를 불러오고 있습니다.</LoadingState>
      )}
      {detail && (
        <JobContent
          detail={detail}
          busy={busy}
          onAction={setPending}
          tab={controller.tab}
          onTabChange={controller.setTab}
          onOpenDataset={controller.openDataset}
        />
      )}
      <HatTextModal
        open={pending !== null}
        label={pending ? labels[pending] : "작업 요청"}
        caption={pending ? descriptions[pending] : ""}
        busy={busy}
        onOkPress={() => void save()}
        onCancelPress={() => {
          if (!busy) setPending(null);
        }}
      />
    </WorkerDetailFrame>
  );
}

function JobContent({
  detail,
  busy,
  onAction,
  tab,
  onTabChange,
  onOpenDataset,
}: {
  detail: WorkerJobDetail;
  busy: boolean;
  onAction: (action: DiagnosticJobAction) => void;
  tab: JobDetailTab;
  onTabChange: (tab: JobDetailTab) => void;
  onOpenDataset: () => void;
}) {
  const { job } = detail;
  const name = detail.kind === "upload" ? detail.job.display_name : detail.job.dataset_display_name;
  return (
    <div className={styles.content}>
      <div className={styles.identity}>
        <h2>{name}</h2>
        <p className={styles.mono}>작업 UUID: {job.uuid}</p>
      </div>
      <div className={styles.metrics}>
        <WorkerMetric label="상태" value={workerJobStatusLabel(job.status)} />
        <WorkerMetric
          label={detail.kind === "upload" ? "반입 확정" : "진단 완료"}
          value={
            detail.kind === "upload"
              ? detail.job.import_episode_count === null
                ? "규모 확인 중"
                : `${detail.job.commit_cursor} / ${detail.job.import_episode_count}`
              : `${detail.job.completed_count} / ${detail.job.requested_count}`
          }
        />
        <WorkerMetric label="변경 시각" value={time(job.modified_at)} />
      </div>
      <ContentSwitcher
        label="작업 상세 정보"
        value={tab}
        onValueChange={(next) => onTabChange(next === "details" ? "details" : "overview")}
        options={[
          { value: "overview", label: "개요" },
          { value: "details", label: detail.kind === "upload" ? "청크·파일" : "진단·구간" },
        ]}
      />
      {tab === "overview" ? (
        <div className={styles.sections}>
          {(detail.kind === "diagnostic" || job.dataset_uuid !== null) && (
            <WorkerDetailSection title="대상 Dataset">
              <Fact label="이름">{name}</Fact>
              <Fact label="Dataset UUID" mono>
                {detail.job.dataset_uuid}
              </Fact>
              <IconButton
                text="Dataset 상세 보기"
                size="sm"
                variant="surface"
                disabled={busy}
                onClick={onOpenDataset}
              />
            </WorkerDetailSection>
          )}
          {detail.kind === "diagnostic" && (
            <WorkerDetailSection title="운영">
              <div className={styles.actions}>
                {(["cancel", "restart", "resume"] as const).map((action) => (
                  <IconButton
                    key={action}
                    text={labels[action]}
                    size="sm"
                    variant="surface"
                    disabled={busy || !detail.job[`can_${action}`]}
                    onClick={() => onAction(action)}
                  />
                ))}
              </div>
              {(job.status === "cancelling" || job.status === "restarting") && (
                <p role="status">Worker 반영 대기</p>
              )}
            </WorkerDetailSection>
          )}
          <WorkerDetailSection title="진행과 점유">
            {detail.kind === "upload" ? (
              <>
                <Fact label="복사 커서">{detail.job.copy_cursor}</Fact>
                <Fact label="확정 커서">{detail.job.commit_cursor}</Fact>
                <Fact label={workUnitLabel(detail.job.units.kind)}>
                  {formatWorkUnitCounts(detail.job.units)}
                </Fact>
                <Fact label={`전체 ${workUnitLabel(detail.job.units.kind)}`}>
                  {formatWorkUnitTotal(detail.job.units)}
                </Fact>
                <Fact label="점유 세대">{detail.job.import_generation}</Fact>
                <Fact label="점유 횟수">{detail.job.import_attempts}</Fact>
                <Fact label="담당 Worker" mono>
                  {detail.job.worker_id}
                </Fact>
                <Fact label="점유 만료">{time(detail.job.worker_lease_expires_at)}</Fact>
              </>
            ) : (
              <>
                <Fact label="종류">{diagnosticJobKindLabel(detail.job.kind)}</Fact>
                <Fact label="강제 재계산">{detail.job.reset ? "예" : "아니요"}</Fact>
                <Fact label="실패 건수">{detail.job.failed_count}</Fact>
                {/* 구간 작업은 커서·점유·보고 값을 구간마다 가진다. 그래서 이 섹션은 구간 수만 보인다. */}
                {detail.job.units.kind === "window" ? (
                  <>
                    <Fact label={workUnitLabel(detail.job.units.kind)}>
                      {formatWorkUnitCounts(detail.job.units)}
                    </Fact>
                    <Fact label={`전체 ${workUnitLabel(detail.job.units.kind)}`}>
                      {formatWorkUnitTotal(detail.job.units)}
                    </Fact>
                  </>
                ) : (
                  <>
                    <Fact label="담당 Worker" mono>
                      {detail.job.worker_id}
                    </Fact>
                    <Fact label="점유 만료">{time(detail.job.lease_expires_at)}</Fact>
                    <Fact label="진행 커서">{detail.job.progress_cursor}</Fact>
                    <Fact label="점유 횟수">{detail.job.claim_count}</Fact>
                    <Fact label="마지막 보고">{time(detail.job.last_heartbeat_at)}</Fact>
                  </>
                )}
              </>
            )}
          </WorkerDetailSection>
          <WorkerDetailSection title="시각">
            <Fact label="생성">{time(job.created_at)}</Fact>
            <Fact label="변경">{time(job.modified_at)}</Fact>
            <Fact label="요청">{time(job.queued_at)}</Fact>
            <Fact label="시작">{time(job.started_at)}</Fact>
            <Fact label="종료">{time(job.finished_at)}</Fact>
          </WorkerDetailSection>
        </div>
      ) : (
        <div className={styles.content}>
          {detail.kind === "upload" ? (
            <WorkerDetailSection title="파일과 실패 정보">
              <Fact label="전송 크기">{formatBytes(detail.job.declared_size_bytes)}</Fact>
              <Fact label="전송 파일">{detail.job.staged_file_count}</Fact>
              <Fact label="반입 파일">{detail.job.import_file_count}</Fact>
              <Fact label="전송 검증">{detail.job.upload_verified ? "확인됨" : "대기 중"}</Fact>
              <Fact label="Storage">{detail.job.target_backend}</Fact>
              <Fact label="실패 단계">
                {detail.job.failure ? workerJobStatusLabel(detail.job.failure.stage) : "—"}
              </Fact>
              <Fact label="실패 코드" mono>
                {detail.job.failure?.code}
              </Fact>
            </WorkerDetailSection>
          ) : (
            <WorkerDetailSection title="종결 사유">
              <Fact label="사유" mono>
                {detail.job.reason ?? "사유 없음"}
              </Fact>
            </WorkerDetailSection>
          )}
          {detail.kind === "diagnostic" && detail.job.units.kind === "window" && (
            <DiagnosticWindowTable jobUuid={detail.job.uuid} />
          )}
          {detail.kind === "upload" && <UploadChunkTable jobUuid={detail.job.uuid} />}
        </div>
      )}
    </div>
  );
}
